import { Chain, listSupportedCurrencies } from "@paywithglide/glide-js";
import ModalBackButton from "../components/ModalBackButton";
import SecondaryButton from "../components/SecondaryButton";
import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import { useState, useEffect, useMemo } from "react";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { LoaderCircle } from "lucide-react";
import {
  arbitrum,
  base,
  berachain,
  bitcoin,
  bitcoinTestnet,
  bsc,
  ethereum,
  hypercore,
  hyperevm,
  optimism,
  polygon,
  polygonAmoy,
  solana,
  solanaDevnet,
  unichain,
} from "@paywithglide/glide-js/chains";

const depositChains = [
  ethereum,
  base,
  polygon,
  optimism,
  arbitrum,
  berachain,
  unichain,
  hyperevm,
  hypercore,
  polygonAmoy,
  bitcoin,
  bitcoinTestnet,
  solana,
  solanaDevnet,
] as const;

const withdrawChains = [
  ethereum,
  base,
  polygon,
  optimism,
  arbitrum,
  berachain,
  polygonAmoy,
  bsc,
  solana,
  solanaDevnet,
] as const;

type Props = {
  onBack?: () => void;
  onSelectChain: (chain: Chain) => void;
};

export default function TransferChooseChain({ onBack, onSelectChain }: Props) {
  const depositConfig = useGlideDepositConfig();

  const [selectedChain, setSelectedChain] = useState<Chain | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredChains, setFilteredChains] = useState<Chain[]>([]);
  const [isLoadingChains, setIsLoadingChains] = useState(false);

  const allChains = useMemo(
    () =>
      (depositConfig.mode === "withdraw" && depositConfig.withdrawChains?.length
        ? depositConfig.withdrawChains
        : depositConfig.mode === "withdraw"
          ? withdrawChains
          : (depositConfig.chainIds?.length
              ? depositConfig.chainIds.map(
                  (id) => depositChains.find((c) => c.id === id)!,
                )
              : depositChains
            ).filter(
              (c) =>
                (!depositConfig.chainIds?.length ||
                  depositConfig.chainIds?.includes(c.id)) &&
                !depositConfig.excludeChainIds?.includes(c.id),
            )
      )
        .filter((c) =>
          depositConfig.chain?.isTestnet ? c.isTestnet : !c.isTestnet,
        )
        .filter((c) => {
          // Filter out Bitcoin chains if tier2 currency tier is excluded
          const isBitcoinChain =
            c.id === bitcoin.id || c.id === bitcoinTestnet.id;
          if (
            isBitcoinChain &&
            depositConfig.excludeCurrencyTiers?.includes("tier2")
          ) {
            return false;
          }
          return true;
        }),
    [
      depositConfig.mode,
      depositConfig.withdrawChains,
      depositConfig.chainIds,
      depositConfig.excludeChainIds,
      depositConfig.chain,
      depositConfig.excludeCurrencyTiers,
    ],
  );

  // Filter chains based on allowedPaymentCurrencies
  useEffect(() => {
    const filterChainsByCurrency = async () => {
      if (!depositConfig.allowedPaymentCurrencies?.length) {
        // No filtering needed, use all chains
        setFilteredChains(allChains);
        setIsLoadingChains(false);
        return;
      }

      setIsLoadingChains(true);

      try {
        // Get all currencies across all chains in ONE API call
        const allCurrencies = await listSupportedCurrencies(
          depositConfig.glideConfig,
          {
            chainIds: allChains.map(
              (chain) => chain.id,
            ) as `eip155:${number}`[],
            all: true,
          },
        );

        // Find which chains support the allowed currencies
        const chainsWithAllowedCurrencies: Chain[] = [];

        for (const chain of allChains) {
          // Check if any currency from this chain matches allowed currencies
          const chainSupportsAllowedCurrency = allCurrencies.some(
            (currency) => {
              if (
                !depositConfig.allowedPaymentCurrencies!.includes(currency.id)
              ) {
                return false;
              }

              // Check if this currency is available on this specific chain
              try {
                currency.on(chain);
                return true; // If currency.on(chain) doesn't throw, currency is supported on this chain
              } catch {
                // Currency is not supported on this chain
                return false;
              }
            },
          );

          if (chainSupportsAllowedCurrency) {
            chainsWithAllowedCurrencies.push(chain);
          }
        }

        setFilteredChains(chainsWithAllowedCurrencies);
      } catch {
        // If we can't check, show all chains (conservative approach)
        setFilteredChains(allChains);
      } finally {
        setIsLoadingChains(false);
      }
    };

    filterChainsByCurrency();
  }, [
    depositConfig.allowedPaymentCurrencies,
    allChains,
    depositConfig.glideConfig,
  ]);

  const onClick = async (chain: Chain) => {
    setSelectedChain(chain);
    setIsLoading(true);
    onSelectChain(chain);
  };

  return (
    <>
      {onBack ? <ModalBackButton onClick={() => onBack()} /> : null}

      {depositConfig.mode === "withdraw" ? (
        <TransactionCheckoutHeader title="Destination chain" />
      ) : (
        <TransactionCheckoutHeader
          title={
            depositConfig.mode === "deposit"
              ? "Deposit chain"
              : "Transfer chain"
          }
        />
      )}

      <div>
        <div className={`font-medium text-sm text-[--color-text-primary]`}>
          Choose a chain
        </div>
        <div className="text-[--color-text-secondary] text-xs">
          Use any chain, we'll handle the conversion
        </div>
      </div>

      <div className="mt-2 space-y-2">
        {isLoadingChains ? (
          <div className="flex items-center justify-center py-8">
            <LoaderCircle className="h-8 w-8 animate-spin text-[--color-text-secondary]" />
          </div>
        ) : (
          filteredChains.map((c) => (
            <SecondaryButton
              key={c.id}
              icon={
                <img
                  src={c.logoUrl}
                  alt={`${c.name} Logo`}
                  className="h-5 w-5 rounded-full"
                />
              }
              title={c.name}
              onClick={() => onClick(c)}
              disabled={isLoading}
              loading={c.id === selectedChain?.id && isLoading}
            />
          ))
        )}
      </div>
    </>
  );
}
